import MasterService from '../common/master.service';
import userRes from '../common/resources/user.resource';
import {noop} from '../common/common.utils';
import {parseQueryString} from '../common/query.utils';
import debugAuthModalMod from './modals/debug-auth.modal';
import AnalyticsService from '../analytics/analytics.service';
import flashMessageServiceMod from '../../common/flashmessageservice/flashmessageservice.module';
import logoutModalMod from './modals/logout.modal';


// Key to use for user subscriptions
const USER_SUBSCRIPTION = Symbol('usersubscription');

const LOGIN_ELEMENTS = [
  '.login-button',
];
const LOGOUT_ELEMENTS = [
  ".logout-button",
  ".logout-link"
];

const CSS_DISPLAY_PROPERTY = 'display';
const CSS_DISPLAY_SHOW = [
  CSS_DISPLAY_PROPERTY,
  'inline-flex'
];
const CSS_DISPLAY_HIDE = [
  CSS_DISPLAY_PROPERTY,
  'none'
];

class AuthService extends MasterService {

  constructor($log, $q, $timeout, Raven, gettextCatalog, UserResource, Analytics, openLogoutModal, FlashMessageService) {
    'ngInject';

    super();

    this.$log = $log;
    this.$q = $q;
    this.$timeout = $timeout;
    this.UserResource = UserResource;
    this.Raven = Raven;
    this.gettextCatalog = gettextCatalog;
    this.Analytics = Analytics;
    this.FlashMessageService = FlashMessageService;
    this.openLogoutModal = openLogoutModal;


    /**
     * Has auth status been fetched from backend?
     * @type {boolean}
     */
    this.synced = false;

    /**
     * Is the user authenticated?
     * @type {boolean}
     */
    this.authenticated = false;

    /**
     * Currently authenticated user, if available.
     * @type {?User}
     */
    this.user = null;

    this._addKey(USER_SUBSCRIPTION);

    // Automatically listen to changes from other bundles
    // Need to get value directly from master service, if user is given as a parameter
    // data can be outdated
    this.subscribeUser(() => {
      this.$timeout(() => {
        this._setLocalUser(this._getValue(USER_SUBSCRIPTION));
      });
    }, true);
  }

  /**
   * Subscribe to receive a callback whenever the user changes. The callback will receive the user model or null if the
   * user is not authenticated.
   *
   * Callback should execute all code inside $timeout so the changes are picked up by Angular.
   *
   * @param {function(?User)} callback Callback method
   * @param {boolean} priority If the subscriber should be among the first to be called, give `true`.
   */
  subscribeUser(callback, priority=false) {
    this._subscribe(USER_SUBSCRIPTION, callback, priority);
  }

  /**
   * Stop getting callback notifications when the user changes. The same callback function instance should be given so
   * it is correctly matched to the subscription.
   * @param {function(?User)} callback
   */
  unsubscribeUser(callback) {
    this._unsubscribe(USER_SUBSCRIPTION, callback);
  }

  /**
   * Get the currently authenticated user. If the user model has already been retrieved, the cached model will be
   * returned unless force_refresh is given.
   *
   * @param {boolean} force_refresh Force refresh of user model from backend even if it is cached.
   * @param {boolean} extra_data Fetch extra user data from Podio.
   * @returns {Promise.<User>} Resolves with user model or rejects if user is not authenticated.
   */
  getUser(force_refresh=false, extra_data=false) {
    if (this.synced && !force_refresh) {
      return this.$q.when(this.user);
    }

    return this.UserResource.get(extra_data)
    .then(u => {

      // If 'nr' is given in query string, treat user as unregistered on retrieval
      if ('nr' in parseQueryString()) {
        u = u.copy();
        u.is_registered = false;
        u.is_valid = false;
        u.makeImmutable();
      }

      this._setUser(u);
      return u;
    })
    .catch((...args) => {
      this._unsetUser();
      return this.$q.reject(...args);
    })
    .finally(() => {
      this.synced = true;
    });
  }

  /**
   * Update the currently logged in user's information in the backend. This is also propagated to all other apps that
   * are subscribed to AuthService.
   * @param {User} user
   * @returns {Promise.<User>} Resolves with the updated user model if successful, rejects otherwise.
   */
  updateUser(user) {
    return this.UserResource.update(user).then(new_user => {
      this._setUser(new_user);
      return new_user;
    });
  }

  /**
   * Attempt to log in with the specified credentials.
   *
   * @param {string} username
   * @param {string} password
   * @returns {Promise.<User>} Resolves with user model or rejects if login is not accepted.
   */
  login(username, password) {
    return this.UserResource.login(username, password).then(u => {
      this._setUser(u);
      return u;
    });
  }

  logoutWithModal(additionalOperations = []) {
    this.$timeout(() => {
      return this.openLogoutModal().result
        .then(() => this.logout())
        .then(() => this._performAdditionalOperationsAfterLogout(additionalOperations))
        .then(() => this.FlashMessageService.addMessage(this.gettextCatalog.getString('Olet kirjautunut ulos.'), 'success'))
        .catch(noop);
    });

    return false;
  }

  /**
   * Log out the current user.
   *
   * @returns {Promise} Resolves with null when logout is done, rejects in case of error.
   */
  logout() {
    return this.UserResource.logout().then(() => {
      this._unsetUser();
      return null;
    }).catch((err) => {
      this.$log.error('Logout failed', err);
      this.FlashMessageService.addMessage(`${this.gettextCatalog.getString('Uloskirjautuminen epäonnistui.')} ${this.gettextCatalog.getString('Ole hyvä ja yritä uudelleen myöhemmin.')}`, 'error');
      return this.$q.reject(err);
    });
  }

  _toggleElementVisibility(elements, show) {
    elements.forEach((selector) => {
      const foundElements = document.querySelectorAll(selector)

      foundElements.forEach((element) => {
        const angularElement = angular.element(element);
        if (show == true) {
          angularElement.css(...CSS_DISPLAY_SHOW)
        } else {
          angularElement.css(...CSS_DISPLAY_HIDE)
        }
      });
    });
  }
  _toggleLoginAndLogoutVisibility(show = true) {
    // There are multiple logout elements (links or buttons)
    // Show or hide all of them
    this._toggleElementVisibility(LOGOUT_ELEMENTS, show);
    // Vice versa for login elements
    this._toggleElementVisibility(LOGIN_ELEMENTS, !show);
  }

  _performAdditionalOperationsAfterLogout(operationsList) {
    operationsList.forEach((operations) => {
      if (Array.isArray(operations)) {
        let [obj, func, params] = operations;
        if (obj && func) {
          if (!Array.isArray(params)) {
            params = []
          }
          this.$log.debug('Executing function', func, 'of', obj, 'with params', ...params);
          obj[func](...params);
        }
      } else if (typeof operations == 'function') {
        operations();
      }
    })
  }

  // Set the active user, propagating to other apps. Sets synced bit if not set to false
  _setUser(u, synced=true) {
    this._setLocalUser(u, synced);
    this.Raven.setUserContext({
      id: u.id
    });
    this._setValue(USER_SUBSCRIPTION, u);
    this._toggleLoginAndLogoutVisibility(true);
    this.Analytics.setUser(u);
  }

  // Clear user information, propagating to other apps
  _unsetUser() {
    this._setLocalUser(null);
    this.$log.debug('User data cleared');
    this.Raven.setUserContext();
    this._setValue(USER_SUBSCRIPTION, null);
    this._toggleLoginAndLogoutVisibility(false);
    this.Analytics.setUser();
  }

  reportChange(u) {
    try {
      this.Analytics.onUserChanged(u);
    } catch(e) {
      this.$log.error(e);
    }
  }

  // Set the given user as the new local user in the AuthService. This does not have an effect on the user in other
  // subscribed apps. If null is given, unsets user instead. Sets synced bit unless synced param is set to false.
  _setLocalUser(u, synced=true) {
    const oldUser = this._getValue(USER_SUBSCRIPTION);

    const hasNewUser = u != null;
    const hasOldUser = oldUser != null;

    // Either of the users is null while the other is not (newUser XOR oldUser)
    if (synced && ((hasNewUser && !hasOldUser) || (!hasNewUser && hasOldUser))) {
      this.reportChange(u);
    }
    // Both users are not null but different. Happens when user changes their details.
    if (oldUser != null && u != null && u.id !== oldUser.id) {
      this.reportChange(u);
    }
    this.synced = synced;
    this.user = u;
    this.authenticated = u != null;
  }
}

/**
 * To prevent state changes from getting applied to different apps using the auth service
 * (like login button and oma oral), return the same singleton instance from window reference.
 * @return {AuthService}
 */
function authServiceFactory($log, $q, $timeout, Raven, gettextCatalog, UserResource, Analytics, openLogoutModal, FlashMessageService) {
  'ngInject';

  if (window.authService != null) {
    $log.debug("AuthServiceFactory: already exists");
  }
  else {
    $log.debug("AuthServiceFactory: creating new");
    window.authService = new AuthService($log, $q, $timeout, Raven, gettextCatalog, UserResource, Analytics, openLogoutModal, FlashMessageService);
  }

  return window.authService;
}

const mod = angular.module('app.auth.authservice', [
  userRes, AnalyticsService, logoutModalMod, flashMessageServiceMod
])

.factory('AuthService', authServiceFactory);

/**
 * Fetch user's authentication status from the backend into the service.
 *
 * If user is not authenticated and ?dl is given in URL, open debug auth modal.
 */
function fetchUserFromBackend(AuthService, openDebugAuthModal) {
  'ngInject';

  AuthService.getUser()
  .catch(noop)
  .finally(() => {
    if ('dl' in parseQueryString() && !AuthService.authenticated) {
      openDebugAuthModal().result
      .then(({username, password}) => {
        return AuthService.login(username, password);
      })
      .catch(noop);
    }
  });
}
fetchUserFromBackend.imports = [mod.name, debugAuthModalMod];

export {fetchUserFromBackend, USER_SUBSCRIPTION};
export default mod.name;



// WEBPACK FOOTER //
// ./src/app/auth/auth.service.js